import { useQuery } from '@apollo/client'
import {
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Box,
} from '@mui/material'
import { useState } from 'react'
import { GET_MAINTENANCE_ITEMS } from './queries'

interface MaintenanceItem {
  id: string
  title: string
  ataCode: string
  manufactureCode: string
  description: string
  ataMfgCodeNormalized: string
}

interface MaintenanceItemsListProps {
  aircraftId: string
  orgSlug: string
  selectedTask: MaintenanceItem | null
  onTaskSelect: (task: MaintenanceItem | null) => void
  excludeTaskId?: string | null
}

const normalize = (str: string) =>
  (str || '').replace(/[\s-]/g, '').toLowerCase()

const MaintenanceItemsList = ({
  aircraftId,
  orgSlug,
  selectedTask,
  onTaskSelect,
  excludeTaskId,
}: MaintenanceItemsListProps) => {
  const { data, loading, error } = useQuery(GET_MAINTENANCE_ITEMS, {
    variables: { aircraftId, orgSlug },
  })
  const [search, setSearch] = useState('')

  if (loading) {
    return <CircularProgress size={24} />
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading maintenance items: {error.message}
      </Alert>
    )
  }

  const maintenanceItems: MaintenanceItem[] = data?.maintenanceItems || []
  const normalizedSearch = normalize(search)

  const filteredItems = maintenanceItems
    .filter((item) => item.id !== excludeTaskId)
    .filter((item) => {
      if (!normalizedSearch) return true
      return (
        item.ataMfgCodeNormalized.includes(normalizedSearch) ||
        normalize(item.ataCode).includes(normalizedSearch) ||
        normalize(item.manufactureCode).includes(normalizedSearch) ||
        (item.title || '').toLowerCase().includes(search.toLowerCase())
      )
    })

  return (
    <Box>
      <TextField
        label="Search by ATA/MFG Code or Title"
        size="small"
        fullWidth
        margin="dense"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="e.g. 27-50, 2750, or title"
        sx={{ mb: 1 }}
      />
      {filteredItems.length === 0 ? (
        <Typography color="text.secondary">
          No maintenance items found.
        </Typography>
      ) : (
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                selected={selectedTask?.id === item.id}
                onClick={() =>
                  onTaskSelect(selectedTask?.id === item.id ? null : item)
                }
              >
                <ListItemText
                  primary={item.title}
                  secondary={
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        ATA: {item.ataCode} | MFG: {item.manufactureCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {item.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ataMfgCodeNormalized: {item.ataMfgCodeNormalized}
                      </Typography>
                    </div>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  )
}

export default MaintenanceItemsList
